// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Button, Flex, Heading, IconRefreshLine, Spinner, Table, Text, View } from '@instructure/ui';
import { useEffect, useState } from 'react';
// Components, Layouts, Pages
import Lirn from '../home/component/lirn/Lirn';
// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { getDetailQuiz, startQuiz } from '~/thunks/quiz/quizThunk';
import { formatDateTimeLabel } from '~/utils/helper/common';
import { QuizSubmissionWorkflowStateTypeEnum, QuizTypeEnum } from '~/utils/enum/common';
import { studentRoute } from '~/utils/routes/student';
import { SECOND_PER_LIMIT } from '~/utils/constants/common';
// Styles, images, icons
import styles from './QuizDetail.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const QuizDetail = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/quiz', 'common/base']);
  const dispatch = useAppDispatch();
  const { courseId, quizId } = useParams();
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  const { quizDetail, isLoading, errorMessage } = useAppSelector((state) => state.quizState);
  //#endregion Selector

  //#region Declare State
  const [isStartLoading, setIsStartLoading] = useState(false);
  const allowedAttempts = quizDetail?.allowedAttempts ?? 0;
  const totalAttempts = quizDetail?.submissions?.length ?? 0;
  const isDoingQuiz = quizDetail?.submissions?.some(
    (s) => s.workflowState === QuizSubmissionWorkflowStateTypeEnum.UN_TAKEN
  );
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetDetailQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailQuiz = async () => {
    if (!courseId || !quizId) return;

    dispatch(getDetailQuiz({ courseId: courseId, quizId: quizId })).unwrap();
  };

  const handleClickActionQuiz = async () => {
    if (!courseId || !quizId) return;

    setIsStartLoading(true);
    try {
      const response = await dispatch(getDetailQuiz({ courseId: courseId, quizId: quizId })).unwrap();
      const quizData = response.data;
      const allowedAttempts = quizData?.allowedAttempts ?? 0;
      const totalAttempts = quizData?.submissions?.length ?? 0;
      const isAllowedAttempt = totalAttempts < allowedAttempts || allowedAttempts < 0;
      const isDoingQuiz = quizData?.submissions?.some(
        (s) => s.workflowState === QuizSubmissionWorkflowStateTypeEnum.UN_TAKEN
      );
      let isNavigateTakingScreen =
        !quizData.locked && (isDoingQuiz || (!isDoingQuiz && isAllowedAttempt && quizData.hasAccessCode));

      if (!isDoingQuiz && !quizData.hasAccessCode && !quizData.locked && isAllowedAttempt) {
        await dispatch(startQuiz({ courseId: courseId, quizId: quizId }))
          .unwrap()
          .then(() => {
            isNavigateTakingScreen = true;
          });
      }

      if (isNavigateTakingScreen) {
        navigate(
          `${studentRoute.base}${studentRoute.course}/${courseId}${studentRoute.quiz}/${quizId}${studentRoute.takeQuiz}`
        );
      }
    } catch (e) {
    } finally {
      setIsStartLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const isLoadingState = isLoading && (
    <div className={cx('centerContainer')}>
      <Spinner size='small' />
    </div>
  );

  const errorState = errorMessage && (
    <div className={cx('centerContainer')}>
      <Button onClick={handleGetDetailQuiz} size='medium' color='primary' renderIcon={<IconRefreshLine />}>
        {t('retry_button_label', { ns: 'common/base' })}
      </Button>
    </div>
  );

  const getActionButtonLabel = () => {
    if (isDoingQuiz) return t('quiz_resume_button');
    if (totalAttempts > 0 && (allowedAttempts < 0 || totalAttempts < allowedAttempts)) {
      return t('quiz_take_again_button');
    }
    return t('quiz_take_button');
  };

  const renderAvailableDate = () => {
    const unlock = quizDetail?.unlockDate;
    const lock = quizDetail?.lockDate;

    if (!unlock && !lock) return null;

    let label = '';
    const unlockLabel = formatDateTimeLabel(unlock ?? '', t('common_at_time_label', { ns: 'common/base' }), true);
    const lockLabel = formatDateTimeLabel(lock ?? '', t('common_at_time_label', { ns: 'common/base' }), true);
    if (unlock && lock) {
      label = `${unlockLabel} - ${lockLabel}`;
    } else if (unlock) {
      label = unlockLabel;
    } else if (lock) {
      label = lockLabel;
    }

    return (
      <li>
        <Text size='medium' weight='bold'>
          {t('quiz_available_label')}
        </Text>
        <View margin='0 x-large 0 x-small'>
          <Text size='medium'>{label}</Text>
        </View>
      </li>
    );
  };

  const getHeaderCells = () => {
    const headerCells = [
      <Table.ColHeader id='Attempt' key='attempt'>
        {t('quiz_attempt_label')}
      </Table.ColHeader>,
      <Table.ColHeader id='Time' key='time'>
        {t('quiz_time_label')}
      </Table.ColHeader>,
    ];

    if (quizDetail?.quizType !== QuizTypeEnum.SURVEY) {
      headerCells.push(
        <Table.ColHeader id='Score' key='score'>
          {t('quiz_score_label')}
        </Table.ColHeader>
      );
    }

    return headerCells;
  };

  const getBodyRows = () => {
    if (!quizDetail?.submissions?.length) return null;

    return [...quizDetail.submissions].reverse().map((data, index) => {
      const minuteTimeSpent = Math.round(data.timeSpent / SECOND_PER_LIMIT);
      let timeSpentLabel = t('quiz_time_spent_minute_plural_label', { minute: minuteTimeSpent });

      if (minuteTimeSpent === 0) {
        timeSpentLabel = t('quiz_time_spent_less_minute_label', { minute: 1 });
      } else if (minuteTimeSpent === 1) {
        timeSpentLabel = t('quiz_time_spent_minute_label', { minute: minuteTimeSpent });
      }

      const cells = [
        <Table.Cell key='attempt'>
          <span>
            {t('quiz_attempt_label')} {data.attempt}
          </span>
        </Table.Cell>,
        <Table.Cell key='time'>
          <span>{timeSpentLabel}</span>
        </Table.Cell>,
      ];

      if (quizDetail?.quizType !== QuizTypeEnum.SURVEY) {
        cells.push(
          <Table.Cell key='score'>
            <span>
              {t('quiz_score_result_label', {
                score: data.score,
                possibleScore: data.quizPointsPossible ?? quizDetail?.pointsPossible,
              })}
            </span>
          </Table.Cell>
        );
      }

      return <Table.Row key={index}>{cells}</Table.Row>;
    });
  };

  const renderQuizDetail = () => {
    if (isLoading && !isStartLoading) return isLoadingState;

    if (errorMessage && !isStartLoading) return errorState;

    const timeLimit = quizDetail?.timeLimit ?? 0;
    const timeLimitLabel =
      timeLimit > 1
        ? t('quiz_time_spent_minute_plural_label', { minute: timeLimit })
        : t('quiz_time_spent_minute_label', { minute: timeLimit });

    return (
      <>
        <Text size='large' weight='bold'>
          {quizDetail?.title}
        </Text>

        <ul className={cx('quizDetailMeta')}>
          <li>
            <Text size='medium' weight='bold'>
              {t('quiz_due_label')}
            </Text>

            <View margin='0 x-large 0 x-small'>
              <Text size='medium'>
                {quizDetail?.dueDate
                  ? formatDateTimeLabel(quizDetail?.dueDate, t('common_at_time_label', { ns: 'common/base' }), true)
                  : t('quiz_no_due_label')}
              </Text>
            </View>
          </li>

          {quizDetail?.quizType !== QuizTypeEnum.SURVEY && (
            <li>
              <Text size='medium' weight='bold'>
                {t('quiz_point_label')}
              </Text>

              <View margin='0 x-large 0 x-small'>
                <Text size='medium'>{quizDetail?.pointsPossible}</Text>
              </View>
            </li>
          )}

          <li>
            <Text size='medium' weight='bold'>
              {t('quiz_question_label')}
            </Text>

            <View margin='0 x-large 0 x-small'>
              <Text size='medium'>{quizDetail?.questionCount}</Text>
            </View>
          </li>

          {renderAvailableDate()}

          <li>
            <Text size='medium' weight='bold'>
              {t('quiz_time_limit_label')}
            </Text>

            <View margin='0 x-large 0 x-small'>
              <Text size='medium'>{quizDetail?.timeLimit ? timeLimitLabel : t('quiz_none_label')}</Text>
            </View>
          </li>

          <li>
            <Text size='medium' weight='bold'>
              {t('quiz_allowed_attempts_label')}
            </Text>

            <View margin='0 x-large 0 x-small'>
              <Text size='medium'>
                {(quizDetail?.allowedAttempts ?? 0) < 0
                  ? t('quiz_allowed_attempts_unlimited_label')
                  : quizDetail?.allowedAttempts}
              </Text>
            </View>
          </li>
        </ul>

        <Text size='large' weight='bold'>
          {t('quiz_instructions_label')}
        </Text>

        <div dangerouslySetInnerHTML={{ __html: quizDetail?.description ?? '' }} />

        {!quizDetail?.locked && (isDoingQuiz || totalAttempts < allowedAttempts || allowedAttempts < 0) && (
          <Flex justifyItems='center' margin='large 0 0 0'>
            <Button color='primary' type='button' onClick={handleClickActionQuiz} disabled={isStartLoading}>
              <Flex justifyItems='center' alignItems='center' gap='small'>
                <span>{getActionButtonLabel()}</span>
                {isStartLoading && <Spinner size='x-small' />}
              </Flex>
            </Button>
          </Flex>
        )}

        {quizDetail?.locked && quizDetail?.lockDate && (
          <Text size='medium'>
            {t('quiz_locked_label', {
              time: formatDateTimeLabel(quizDetail?.lockDate, t('common_at_time_label', { ns: 'common/base' }), true),
            })}
          </Text>
        )}
      </>
    );
  };

  const renderAttemptHistory = () => {
    if (isLoading && !isStartLoading) return isLoadingState;

    if (errorMessage && !isStartLoading) return errorState;

    if (!quizDetail?.submissions || quizDetail?.submissions?.length === 0) {
      return (
        <div className={cx('centerContainer')}>
          <Text variant='contentSmall'>{t('quiz_no_attempt_history_label')}</Text>
        </div>
      );
    }

    if (isDoingQuiz) {
      return (
        <div className={cx('centerContainer')}>
          <Text variant='contentSmall'>{t('quiz_quiz_in_progress_label')}</Text>
        </div>
      );
    }

    return (
      <>
        <Flex display='flex' direction='row' alignItems='center' justifyItems='center' width={'100%'}>
          <Heading level='h3'>{t('quiz_attempt_history_label')}</Heading>
        </Flex>

        <div className={cx('attemptContainer')}>
          <Table layout='auto'>
            <Table.Head>
              <Table.Row>{getHeaderCells()}</Table.Row>
            </Table.Head>
            <Table.Body>{getBodyRows()}</Table.Body>
          </Table>
        </div>
      </>
    );
  };
  //#endregion Handle Function

  return (
    <div id={cx('quizDetail')} className={cx('container')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('content')}>
        <div className={cx('leftSection')}>{renderQuizDetail()}</div>

        <div className={cx('rightSection')}>
          <div className={cx('rightSectionItem')}>{renderAttemptHistory()}</div>

          <div className={cx('rightSectionItem', 'two')}>{<Lirn />}</div>
        </div>
      </div>
    </div>
  );
};

export default QuizDetail;
